import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';

import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Forecast from 'webapp/models-custom/forecast';

export const settingsNamePrefix = 'forecastsFilterSettings'; // used to save filter preferences

export default class FilterSortForecast extends FilterSort<Forecast> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;

	@tracked override sortProp = 'order';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.ASC;

	override itemDomSelector = 'li.filter-sort-item';

	override searchProps: SearchProps = {
		title: 3,
		'source.title': 1,
		'source.subtitle': 1,
	};

	override settingsNamePrefix = settingsNamePrefix;
	override trackingObjectName = 'forecast';

	constructor(options: FilterSortSettings<Forecast>) {
		super(options);

		this.createFilters();
	}

	createFilters(): void {
		const selectedFilters = this.readSavedSelectedFilters();

		// Created
		this.createFilter<number>({
			id: 1,
			label: 'Created by',
			name: 'creator',
			selected: selectedFilters[1] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.creatorOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((creatorId) => {
					if (creatorId === -1) {
						return !item.isMine;
					} else {
						return item.createdBy === creatorId;
					}
				});
			},
		});

		// Tags (used only)
		this.createFilter<number>({
			id: 2,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[2] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((tagId) => {
					return item.tags.includes(tagId);
				});
			},
		});

		// Data source (used only)
		this.createFilter<string>({
			id: 3,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[3] as string | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((sourceTypeKey) => {
					return item.source?.sourceType?.key === sourceTypeKey;
				});
			},
		});
	}

	@cached
	get creatorOptions(): FilterOption<number>[] {
		const allItemCountByCreator: Record<number, number> = {};
		this.allItems.forEach((item) => {
			const createdBy = item.createdBy;
			if (!allItemCountByCreator[createdBy]) {
				allItemCountByCreator[createdBy] = 0;
			}
			allItemCountByCreator[createdBy]++;
		});

		return this.mmUsersService.getFilterOptionsForUsers(allItemCountByCreator);
	}

	@cached
	get tagOptions(): FilterOption<number>[] {
		const tagsArray: number[] = [];

		this.allItems.forEach((item) => {
			item.tags.forEach((tagId: number) => {
				tagsArray.push(tagId);
			});
		});

		const assignedTags: FilterOption<number>[] = this.mmTagsService.getTagCountList(tagsArray);

		if (assignedTags.length) {
			assignedTags.unshift({ id: 0, text: 'All' });
		}
		return assignedTags;
	}

	@cached
	get datasourceOptions(): FilterOption<string>[] {
		// get how many boards each source is used on
		const sourceTypeArray: string[] = [];

		this.allItems.forEach((goal) => {
			const key = goal.source?.sourceType?.key;
			if (key) {
				sourceTypeArray.push(key);
			}
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}
}
